var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
import { ErrorsPreprocessor } from "./ErrorsPreprocessor.js";
import { EventManager } from "./EventManager.js";
import { ErrorAccessor } from "../accessors/ErrorAccessor.js";
import { getEntityMarker } from "./state/getEntityMarker.js";
class AccessorErrorManager {
  constructor(eventManager, treeStore) {
    __publicField(this, "errorsByState", /* @__PURE__ */ new Map());
    __publicField(this, "getNewErrorId", (() => {
      let errorId = 1;
      return () => errorId++;
    })());
    this.eventManager = eventManager;
    this.treeStore = treeStore;
  }
  hasErrors() {
    return !!this.errorsByState.size;
  }
  getErrors() {
    return Array.from(this.errorsByState.values(), (it) => Array.from(it.values())).flat();
  }
  clearErrors() {
    this.eventManager.syncOperation(() => {
      for (const [stateNode] of this.errorsByState) {
        stateNode.errors = void 0;
        this.eventManager.registerJustUpdated(stateNode, EventManager.NO_CHANGES_DIFFERENCE);
      }
      this.errorsByState.clear();
    });
  }
  clearErrorsByState(state) {
    return this.eventManager.syncOperation(() => {
      if (!this.errorsByState.has(state)) {
        return;
      }
      this.errorsByState.delete(state);
      state.errors = void 0;
      this.eventManager.registerJustUpdated(state, EventManager.NO_CHANGES_DIFFERENCE);
    });
  }
  replaceErrors(data, operations) {
    this.eventManager.syncOperation(() => {
      this.clearErrors();
      const preprocessor = new ErrorsPreprocessor(data, operations);
      const errorTreeRoot = preprocessor.preprocess();
      this.setRootStateErrors(errorTreeRoot);
      this.dumpErrorData(data);
    });
  }
  addError(state, error) {
    return this.eventManager.syncOperation(() => {
      const errorId = this.getNewErrorId();
      let errorsById = this.errorsByState.get(state);
      if (errorsById === void 0) {
        this.errorsByState.set(state, errorsById = /* @__PURE__ */ new Map());
      }
      errorsById.set(errorId, error);
      state.errors = new ErrorAccessor(Array.from(errorsById.values()));
      this.eventManager.registerJustUpdated(state, EventManager.NO_CHANGES_DIFFERENCE);
      return () => this.eventManager.syncOperation(() => {
        const errorsById2 = this.errorsByState.get(state);
        if (errorsById2 === void 0) {
          return;
        }
        errorsById2.delete(errorId);
        if (errorsById2.size) {
          state.errors = new ErrorAccessor(Array.from(errorsById2.values()));
        } else {
          state.errors = void 0;
          this.errorsByState.delete(state);
        }
        this.eventManager.registerJustUpdated(state, EventManager.NO_CHANGES_DIFFERENCE);
      });
    });
  }
  addSeveralErrors(state, errors) {
    if (errors.validation) {
      for (const error of errors.validation) {
        this.addError(state, error);
      }
    }
    if (errors.execution) {
      for (const error of errors.execution) {
        this.addError(state, error);
      }
    }
  }
  setRootStateErrors(errorTreeRoot) {
    for (const rootStates of this.treeStore.subTreeStatesByRoot.values()) {
      for (const [subTreePlaceholder, rootError] of errorTreeRoot) {
        const rootState = rootStates.get(subTreePlaceholder);
        if (!rootState) {
          continue;
        }
        switch (rootState.type) {
          case "entityRealm": {
            this.setEntityStateErrors(rootState, rootError);
            break;
          }
          case "entityList": {
            this.setEntityListStateErrors(rootState, rootError);
            break;
          }
        }
      }
    }
  }
  setEntityStateErrors(state, errors) {
    this.addSeveralErrors(state, errors);
    if (errors.nodeType !== "iNode") {
      return;
    }
    for (const [childKey, child] of errors.children) {
      if (typeof childKey !== "string") {
        continue;
      }
      if (child.nodeType === "leaf") {
        const fieldState = state.children.get(childKey);
        if (fieldState?.type === "field") {
          this.addSeveralErrors(fieldState, child);
          continue;
        }
      }
      const fields = getEntityMarker(state).fields;
      const placeholders = fields.placeholders.get(childKey);
      if (placeholders === void 0) {
        continue;
      }
      const normalizedPlaceholders = typeof placeholders === "string" ? /* @__PURE__ */ new Set([placeholders]) : placeholders;
      for (const normalizedPlaceholder of normalizedPlaceholders) {
        const fieldState = state.children.get(normalizedPlaceholder);
        if (fieldState === void 0) {
          continue;
        }
        switch (fieldState.type) {
          case "entityRealm":
            if (child.nodeType === "iNode" && child.children.has(fieldState.entity.id.value)) {
              this.setEntityStateErrors(fieldState, child.children.get(fieldState.entity.id.value));
            } else {
              this.setEntityStateErrors(fieldState, child);
            }
            break;
          case "entityList":
            this.setEntityListStateErrors(fieldState, child);
            break;
        }
      }
    }
  }
  setEntityListStateErrors(state, errors) {
    this.addSeveralErrors(state, errors);
    if (errors.nodeType !== "iNode") {
      return;
    }
    for (const [childKey, childError] of errors.children) {
      let childState = state.children.get(childKey);
      if (childState === void 0) {
        continue;
      }
      if (childState.type === "entityRealmStub") {
        childState.getAccessor();
        childState = state.children.get(childKey);
      }
      if (childError.nodeType === "iNode") {
        this.setEntityStateErrors(childState, childError);
      }
    }
  }
  dumpErrorData(data) {
    for (const subTreePlaceholder in data) {
      const treeDatum = data[subTreePlaceholder];
      const executionErrors = treeDatum.errors;
      const allErrors = treeDatum?.validation?.errors ? executionErrors.concat(treeDatum.validation.errors) : executionErrors;
      const normalizedErrors = allErrors.map((error) => {
        return {
          path: error.path.map((pathPart) => {
            if (pathPart.__typename === "_FieldPathFragment") {
              return pathPart.field;
            }
            if (pathPart.alias) {
              return `#${pathPart.index}(${pathPart.alias})`;
            }
            return pathPart.index;
          }).join("."),
          type: "type" in error ? error.type : void 0,
          message: typeof error.message === "string" ? error.message : error.message?.text
        };
      });
      if (Object.keys(normalizedErrors).length) {
        console.table(normalizedErrors);
      }
      if (treeDatum.errorMessage) {
        console.error(treeDatum.errorMessage);
      }
    }
  }
}
export {
  AccessorErrorManager
};
//# sourceMappingURL=AccessorErrorManager.js.map
